import React, { useEffect, useMemo, useState } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';

interface ConsultationRecord {
  id?: string;
  name?: string;
  email?: string;
  company?: string;
  industry?: string;
  description?: string;
  preferredTime?: string;
  status?: string;
  timestamp?: string; // created at
  scheduledAt?: string; // planned date/time
  dueAt?: string;
  reminderMinutes?: number;
  color?: string;
}

interface ConsultationsCalendarProps {
  consultations: ConsultationRecord[];
  onAdded?: () => void;
  onAddedRecord?: (record: ConsultationRecord) => void;
  onRefresh?: () => void;
}

// Date helpers (no external deps)
const toStartOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const isSameDay = (a: Date, b: Date): boolean => {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const startOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const startOfWeek = (date: Date): Date => {
  // Sunday as first day of week
  const d = new Date(date);
  const day = d.getDay();
  const diff = day; // 0..6
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = 6 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(23, 59, 59, 999);
  return d;
};

const addMonths = (date: Date, months: number): Date => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

const formatMonthYear = (date: Date): string =>
  date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

const yyyyMmDd = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const tryParseDate = (input?: string): Date | null => {
  if (!input) return null;
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
};

const getEventDate = (record: ConsultationRecord): Date | null => {
  return (
    tryParseDate(record.scheduledAt) ||
    tryParseDate(record.timestamp) ||
    null
  );
};

const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const ConsultationsCalendar: React.FC<ConsultationsCalendarProps> = ({ consultations, onAdded, onAddedRecord, onRefresh }) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState<Date>(toStartOfDay(new Date()));

  const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [viewMode, setViewMode] = useState<'month' | 'agenda'>('month');
  const { supported: pushSupported, permission: pushPermission, subscribed, subscribe } = usePushNotifications();

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 640px)').matches) {
        setViewMode('agenda');
      }
    } catch {}
  }, []);

  // Form state for add modal
  const [form, setForm] = useState<{
    name: string;
    email: string;
    company: string;
    industry: string;
    description: string;
    date: string; // yyyy-mm-dd
    time: string; // HH:mm
    dueDate?: string; // yyyy-mm-dd
    reminderMinutes?: number;
  }>({
    name: '',
    email: '',
    company: '',
    industry: '',
    description: '',
    date: yyyyMmDd(new Date()),
    time: '09:00',
    dueDate: '',
    reminderMinutes: 60,
  });

  useEffect(() => {
    // Keep add form date in sync with selected day
    setForm((prev) => ({ ...prev, date: yyyyMmDd(selectedDate) }));
  }, [selectedDate]);

  const monthDays: Date[] = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end = endOfWeek(endOfMonth(currentMonth));
    const days: Date[] = [];
    const d = new Date(start);
    while (d <= end) {
      days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return days;
  }, [currentMonth]);

  const eventsByDay: Record<string, ConsultationRecord[]> = useMemo(() => {
    const map: Record<string, ConsultationRecord[]> = {};
    for (const c of consultations || []) {
      const date = getEventDate(c);
      if (!date) continue;
      const key = yyyyMmDd(toStartOfDay(date));
      if (!map[key]) map[key] = [];
      map[key].push(c);
    }
    // De-duplicate by id to avoid transient duplicates on optimistic update + reload
    for (const k of Object.keys(map)) {
      const seen: Record<string, boolean> = {};
      map[k] = map[k].filter(item => {
        if (item.id && seen[item.id]) return false;
        if (item.id) seen[item.id] = true;
        return true;
      });
    }
    // Sort events in each day by time
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => {
        const da = getEventDate(a)?.getTime() || 0;
        const db = getEventDate(b)?.getTime() || 0;
        return da - db;
      });
    }
    return map;
  }, [consultations]);

  const eventsForSelected = eventsByDay[yyyyMmDd(selectedDate)] || [];

  // Load time blocks
  const [timeBlocks, setTimeBlocks] = useState<any[]>([]);
  useEffect(() => {
    const loadBlocks = async () => {
      try {
        const pwd = localStorage.getItem('admin-password') || '';
        const token = localStorage.getItem('admin-token') || '';
        const headers: Record<string, string> = {};
        
        if (pwd) headers['x-admin-password'] = pwd;
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        const res = await fetch('/api/admin/timeblocks', { headers });
        const json = await res.json().catch(() => ({}));
        if (json?.success) setTimeBlocks(json.data || []);
      } catch {}
    };
    loadBlocks();
  }, []);

  const openAddModal = (day?: Date) => {
    const target = toStartOfDay(day || selectedDate || new Date());
    setSelectedDate(target);
    setForm((prev) => ({ ...prev, date: yyyyMmDd(target) }));
    setError('');
    setIsAddOpen(true);
  };

  // Snooze/reschedule helpers
  const updateSchedule = async (record: ConsultationRecord, newISO: string) => {
    try {
      const body: ConsultationRecord = { ...record, scheduledAt: newISO, status: 'scheduled' };
      const pwd = localStorage.getItem('admin-password') || '';
      const token = localStorage.getItem('admin-token') || '';
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      
      if (pwd) headers['x-admin-password'] = pwd;
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      await fetch('/api/admin/consultations', {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });
      if (onAdded) onAdded();
    } catch {}
  };

  const snooze = async (record: ConsultationRecord, minutes: number) => {
    const date = getEventDate(record) || new Date();
    const newDate = new Date(date.getTime() + minutes * 60 * 1000);
    await updateSchedule(record, newDate.toISOString());
  };

  const openReschedule = async (record: ConsultationRecord) => {
    const current = getEventDate(record) || new Date();
    const input = prompt('New time (HH:mm) for the same day, or ISO date-time:', current.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    if (!input) return;
    let next: Date | null = null;
    if (/^\d{2}:\d{2}$/.test(input)) {
      const y = current.getFullYear();
      const m = current.getMonth();
      const d = current.getDate();
      const [hh, mm] = input.split(':');
      next = new Date(y, m, d, Number(hh), Number(mm), 0, 0);
    } else {
      const t = new Date(input);
      if (!isNaN(t.getTime())) next = t; else alert('Invalid date/time');
    }
    if (next) await updateSchedule(record, next.toISOString());
  };

  // Drag-to-reschedule support
  const [draggingId, setDraggingId] = useState<string | undefined>();
  const onDragStartEvent = (ev: React.DragEvent, rec: ConsultationRecord) => {
    if (!rec.id) return;
    setDraggingId(rec.id);
    ev.dataTransfer.effectAllowed = 'move';
    ev.dataTransfer.setData('text/plain', rec.id);
  };
  const onDropToDay = async (ev: React.DragEvent, day: Date) => {
    ev.preventDefault();
    const id = ev.dataTransfer.getData('text/plain') || draggingId;
    if (!id) return;
    const all = consultations || [];
    const rec = all.find(r => r.id === id);
    if (!rec) return;
    const original = getEventDate(rec) || new Date();
    const next = new Date(day);
    next.setHours(original.getHours(), original.getMinutes(), 0, 0);
    await updateSchedule(rec, next.toISOString());
    setDraggingId(undefined);
  };
  const onDragOverDay = (ev: React.DragEvent) => { ev.preventDefault(); };

  const submitAdd = async () => {
    try {
      setSaving(true);
      setError('');

      if (!form.name || !form.email) {
        setError('Name and email are required');
        setSaving(false);
        return;
      }

      const scheduledAt = new Date(`${form.date}T${form.time || '09:00'}:00`);
      if (isNaN(scheduledAt.getTime())) {
        setError('Invalid date or time');
        setSaving(false);
        return;
      }

      const body: ConsultationRecord = {
        name: form.name,
        email: form.email,
        company: form.company,
        industry: form.industry,
        description: form.description,
        preferredTime: form.time,
        status: 'scheduled',
        scheduledAt: scheduledAt.toISOString(),
        reminderMinutes: form.reminderMinutes ? Number(form.reminderMinutes) : undefined,
        dueAt: form.dueDate ? new Date(`${form.dueDate}T${form.time || '09:00'}:00`).toISOString() : undefined,
      };

      const pwd = localStorage.getItem('admin-password') || '';
      const token = localStorage.getItem('admin-token') || '';
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      
      if (pwd) headers['x-admin-password'] = pwd;
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch('/api/admin/consultations', {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const result = await res.json().catch(() => ({}));
      if (!res.ok || result?.success === false) {
        throw new Error(result?.error || 'Failed to save');
      }

      // Optimistically reflect the new record in UI
      const saved: ConsultationRecord = result?.data || body;
      const savedDate = tryParseDate(saved.scheduledAt || saved.timestamp || '');
      if (savedDate) {
        setSelectedDate(toStartOfDay(savedDate));
        setCurrentMonth(startOfMonth(savedDate));
      }
      if (onAddedRecord) onAddedRecord(saved);
      setIsAddOpen(false);
      if (onAdded) onAdded();
      try {
        if (pushSupported && pushPermission !== 'granted' && !subscribed) {
          subscribe().catch(() => {});
        }
      } catch {}
    } catch (e: any) {
      setError(e?.message || 'Failed to save consultation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
            aria-label="Previous Month"
          >
            ◀
          </button>
          <button
            onClick={() => setCurrentMonth(startOfMonth(new Date()))}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
          >
            Today
          </button>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
            aria-label="Next Month"
          >
            ▶
          </button>
          <div className="ml-2 text-white font-semibold text-sm sm:text-base">
            {formatMonthYear(currentMonth)}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex bg-gray-700 rounded-lg p-1">
            <button onClick={() => setViewMode('month')} className={`px-3 py-1.5 rounded-md text-sm ${viewMode === 'month' ? 'bg-gray-600 text-white' : 'text-gray-300'}`}>Month</button>
            <button onClick={() => setViewMode('agenda')} className={`px-3 py-1.5 rounded-md text-sm ${viewMode === 'agenda' ? 'bg-gray-600 text-white' : 'text-gray-300'}`}>Today</button>
          </div>
          <button
            onClick={() => onRefresh && onRefresh()}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm flex items-center gap-1"
            title="Refresh calendar data"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={() => openAddModal(selectedDate)}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm"
          >
            Add Consultation
          </button>
          <button
            onClick={async () => {
              const title = prompt('Block title', 'Focus');
              if (!title) return;
              const date = prompt('Date (YYYY-MM-DD)', yyyyMmDd(selectedDate));
              if (!date) return;
              const start = prompt('Start (HH:MM)', '09:00');
              if (!start) return;
              const end = prompt('End (HH:MM)', '11:00');
              if (!end) return;
              const color = prompt('Color hex', '#22c55e') || '#22c55e';
              try {
                const pwd = localStorage.getItem('admin-password') || '';
                const token = localStorage.getItem('admin-token') || '';
                const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                
                if (pwd) headers['x-admin-password'] = pwd;
                if (token) headers['Authorization'] = `Bearer ${token}`;
                
                const res = await fetch('/api/admin/timeblocks', { 
                  method: 'POST', 
                  headers, 
                  body: JSON.stringify({ title, date, start, end, color }) 
                });
                const json = await res.json().catch(() => ({}));
                if (json?.success) {
                  setTimeBlocks((prev) => [...prev, json.data]);
                }
              } catch {}
            }}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
          >Add Block</button>
        </div>
      </div>

      {/* Calendar Grid + Agenda */}
      {viewMode === 'month' ? (
      <div className="overflow-x-auto -mx-2 sm:mx-0">
        <div className="min-w-[320px] sm:min-w-[720px]">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-px bg-gray-700 rounded-t-lg overflow-hidden">
            {dayNamesShort.map((d) => (
              <div key={d} className="bg-gray-800 p-2 text-xs sm:text-sm text-gray-300 text-center font-medium">
                {d}
              </div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-px bg-gray-700 rounded-b-lg overflow-hidden">
            {monthDays.map((day) => {
              const inCurrentMonth = day.getMonth() === currentMonth.getMonth();
              const isSelected = isSameDay(day, selectedDate);
              const events = eventsByDay[yyyyMmDd(day)] || [];
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelectedDate(toStartOfDay(day))}
                  onDragOver={onDragOverDay}
                  onDrop={(e) => onDropToDay(e, day)}
                  className={
                    `relative bg-gray-800 text-left p-1 sm:p-3 h-20 sm:h-32 hover:bg-gray-800/80 focus:outline-none ` +
                    `${!inCurrentMonth ? 'opacity-60' : ''} ` +
                    `${isSelected ? 'ring-2 ring-primary-500 z-10' : ''}`
                  }
                >
                  <div className="flex justify-between items-start">
                    <span className={`text-xs sm:text-sm ${isSelected ? 'text-primary-400' : 'text-gray-200'}`}>
                      {day.getDate()}
                    </span>
                    {events.length > 0 && (
                      <span className="text-[10px] sm:text-xs bg-primary-600/20 text-primary-300 px-1.5 py-0.5 rounded">
                        {events.length}
                      </span>
                    )}
                  </div>
                   <div className="mt-1 sm:mt-2 space-y-0.5 sm:space-y-1 overflow-hidden">
                     {/* Time blocks */}
                     {(timeBlocks || []).filter(b => yyyyMmDd(new Date(b.date)) === yyyyMmDd(day)).slice(0, 1).map((b) => (
                       <div key={b.id} className="truncate text-[8px] sm:text-xs text-gray-200 px-1 sm:px-1.5 py-0.5 rounded" style={{ backgroundColor: `${b.color || '#22c55e'}33` }}>
                         🟩 <span className="hidden sm:inline">{b.title || 'Focus block'} </span>{b.start}–{b.end}
                       </div>
                     ))}
                    {events.slice(0, 2).map((ev) => {
                      const dt = getEventDate(ev);
                      const time = dt ? dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                      return (
                        <div key={ev.id || `${ev.email}-${time}`}
                          draggable
                          onDragStart={(e) => onDragStartEvent(e, ev)}
                          className="truncate text-[8px] sm:text-xs bg-gray-700/60 text-gray-200 px-1 sm:px-1.5 py-0.5 rounded flex items-center gap-1">
                          <span className="inline-block w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full" style={{ backgroundColor: ev.color || (ev.status === 'scheduled' ? '#60a5fa' : ev.status === 'nouveau' ? '#eab308' : '#a3a3a3') }} />
                          <span className="hidden sm:inline">{time} • </span>{ev.name || ev.email || 'Consultation'}
                        </div>
                      );
                    })}
                    {events.length > 2 && (
                      <div className="text-[8px] sm:text-xs text-gray-400">+{events.length - 2} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      ) : (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-3 sm:p-4">
          <h3 className="text-white font-semibold text-sm sm:text-base mb-2">Today</h3>
          <ul className="divide-y divide-gray-700">
            {(eventsByDay[yyyyMmDd(new Date())] || []).map((ev) => {
              const dt = getEventDate(ev);
              const time = dt ? dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
              return (
                <li key={ev.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-white font-medium truncate flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: ev.color || '#60a5fa' }} />
                      {ev.name || ev.email || 'Consultation'}
                    </p>
                    <p className="text-xs text-gray-300 truncate">{ev.email}</p>
                    <p className="text-[10px] text-gray-400">{ev.description}</p>
                  </div>
                  <div className="text-xs text-gray-300 whitespace-nowrap flex items-center gap-2">
                    {time}
                    <button className="px-2 py-1 bg-gray-700 rounded-md hover:bg-gray-600" onClick={() => snooze(ev, 10)}>Snooze</button>
                    <button className="px-2 py-1 bg-gray-700 rounded-md hover:bg-gray-600" onClick={() => openReschedule(ev)}>Reschedule</button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Selected day details */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold text-sm sm:text-base">
            {selectedDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </h3>
          <button
            onClick={() => openAddModal(selectedDate)}
            className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs sm:text-sm"
          >
            Add
          </button>
        </div>

        {eventsForSelected.length === 0 ? (
          <p className="text-gray-400 text-sm">No consultations scheduled</p>
        ) : (
          <ul className="divide-y divide-gray-700">
            {eventsForSelected.map((ev) => {
              const dt = getEventDate(ev);
              const time = dt ? dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
              return (
                <li key={ev.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-white font-medium truncate">{ev.name || ev.email || 'Consultation'}</p>
                    <p className="text-xs text-gray-300 truncate">{ev.email}</p>
                    <p className="text-xs text-gray-400 truncate">{[ev.company, ev.industry].filter(Boolean).join(' • ')}</p>
                  </div>
                  <div className="text-xs text-gray-300 whitespace-nowrap">{time}</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Add modal */}
      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-gray-700 flex items-center justify-between">
              <h4 className="text-white font-semibold">Add Consultation</h4>
              <button onClick={() => setIsAddOpen(false)} className="text-gray-300 hover:text-white">✕</button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {error && (
                <div className="p-2 bg-red-500/20 border border-red-500/40 rounded text-red-300 text-sm">{error}</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-300 text-xs mb-1">Full Name *</label>
                  <input
                    className="w-full p-2 bg-gray-700 rounded-lg text-white text-sm"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-xs mb-1">Email *</label>
                  <input
                    type="email"
                    className="w-full p-2 bg-gray-700 rounded-lg text-white text-sm"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Enter email"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-300 text-xs mb-1">Company</label>
                  <input
                    className="w-full p-2 bg-gray-700 rounded-lg text-white text-sm"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-xs mb-1">Industry</label>
                  <input
                    className="w-full p-2 bg-gray-700 rounded-lg text-white text-sm"
                    value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                    placeholder="Industry"
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-300 text-xs mb-1">Description</label>
                <textarea
                  rows={3}
                  className="w-full p-2 bg-gray-700 rounded-lg text-white text-sm"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Notes, agenda, etc."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-300 text-xs mb-1">Date *</label>
                  <input
                    type="date"
                    className="w-full p-2 bg-gray-700 rounded-lg text-white text-sm"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-xs mb-1">Time *</label>
                  <input
                    type="time"
                    className="w-full p-2 bg-gray-700 rounded-lg text-white text-sm"
                    value={form.time}
                    onChange={(e) => setForm({ ...form, time: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-300 text-xs mb-1">Due date (optional)</label>
                  <input
                    type="date"
                    className="w-full p-2 bg-gray-700 rounded-lg text-white text-sm"
                    value={form.dueDate || ''}
                    onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-xs mb-1">Reminder (minutes before)</label>
                  <input
                    type="number"
                    min={0}
                    className="w-full p-2 bg-gray-700 rounded-lg text-white text-sm"
                    value={String(form.reminderMinutes || '')}
                    onChange={(e) => setForm({ ...form, reminderMinutes: Number(e.target.value || 0) })}
                  />
                </div>
              </div>
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-700 flex items-center justify-end gap-2">
              <button
                onClick={() => setIsAddOpen(false)}
                className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submitAdd}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg text-white text-sm disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationsCalendar;


